import * as React from 'react';
import {SvgIconProps} from '../SvgIcon';
import Tooltip, {TooltipProps} from '../Tooltip/Tooltip';
import LightbulbOnOutline from 'mdi-material-ui/LightbulbOnOutline';
import InformationOutline from 'mdi-material-ui/InformationOutline';
import HelpCircleOutline from 'mdi-material-ui/HelpCircleOutline';
import ClickAwayListener from '../ClickAwayListener';
import {createStyles, withStyles, WithStyles} from '../styles';
import {Omit} from '../index';
import {SvgIconClassKey} from '../SvgIcon';
import * as classNames from 'classnames';

export interface TooltipIconProps extends Omit<SvgIconProps, 'classes'> {
  title: TooltipProps['title'];
  icon?: 'help' | 'info' | 'light-bulb' | React.ComponentType<SvgIconProps>;
  variant?: 'icon' | 'inline';
  spinning?: boolean;
}

const styles = createStyles({
  inline: {
    opacity: 0.75,
    fontSize: '115%',
    verticalAlign: 'text-bottom',
    '&:hover': {
      opacity: 1,
    },
  },
  clicked: {
    opacity: 1,
  },
  [`@keyframes spin`]: {
    from: {transform: 'rotate(0deg)'},
    to: {transform: 'rotate(-359.9deg)'},
  } as any,
  spinning: {
    animation: '$spin 1s infinite linear',
  },
});

class TooltipIcon extends React.PureComponent<
  TooltipIconProps & WithStyles<keyof typeof styles | SvgIconClassKey>,
  {opened: boolean; clicked: boolean}
> {
  static defaultProps = {
    icon: 'help',
    variant: 'icon',
  } as Partial<TooltipIconProps>;

  state = {
    opened: false,
    clicked: false,
  };

  handleClick = () => {
    this.setState({clicked: true});
  };

  handleClickAway = () => {
    this.setState({clicked: false, opened: false});
  };

  handleOpen = () => {
    this.setState({opened: true});
  };

  handleClose = () => {
    this.setState({opened: false});
  };

  render() {
    const {opened, clicked} = this.state;
    let {
      icon,
      title,
      spinning,
      variant,
      classes: {clicked: clickedClass, inline, spinning: spinningClass, ...classes},
      className,
      ref,
      ...otherProps
    } = this.props;
    let Icon;
    switch (icon) {
      case 'help':
        Icon = HelpCircleOutline;
        break;
      case 'info':
        Icon = InformationOutline;
        break;
      case 'light-bulb':
        Icon = LightbulbOnOutline;
        break;
      default:
        Icon = icon;
        break;
    }
    const Inner = React.forwardRef((props, ref) => (
      <Tooltip
        open={opened || clicked}
        enterDelay={50}
        title={title}
        onOpen={this.handleOpen}
        onClose={this.handleClose}
      >
        <Icon
          onClick={this.handleClick}
          className={classNames(className, {
            [clickedClass]: clicked,
            [inline]: variant === 'inline',
            [spinningClass]: spinning,
          })}
          classes={classes}
          ref={ref}
          {...props}
        />
      </Tooltip>
    ));
    return (
      <ClickAwayListener onClickAway={this.handleClickAway}>
        <Inner {...otherProps} />
      </ClickAwayListener>
    );
  }
}

export default withStyles(styles)(TooltipIcon);
